/**
 * Centralized Image Upload Service for Frontend
 *
 * This service provides reusable utilities for image uploads throughout the application.
 * All images are stored in: D:\Projects\GoTech\Git\GitDesktop\uploads
 *
 * Categories:
 * - profile: User profile photos and merchant owner photos
 * - merchant: Merchant business logos and other merchant-related images
 * - products: Product images (for future use)
 */

import axios from 'axios';
import api from '../api/axiosInstance';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || API_BASE_URL;
const MAX_FILE_SIZE = parseInt(process.env.REACT_APP_MAX_FILE_SIZE || 5242880); // 5MB
const ALLOWED_FORMATS = process.env.REACT_APP_ALLOWED_IMAGE_FORMATS || 'jpeg,jpg,png,webp,avif';

/**
 * Validate image file before upload
 * @param {File} file - The file object from input
 * @returns {object} { valid: boolean, error: string | null }
 */
export const validateImageFile = (file) => {
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (MAX_FILE_SIZE / (1024 * 1024)).toFixed(2);
    return {
      valid: false,
      error: `File size exceeds limit of ${sizeMB}MB. File size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`
    };
  }

  // Check file format
  const allowedFormats = ALLOWED_FORMATS.split(',').map(f => f.trim());
  const fileExtension = file.name.split('.').pop().toLowerCase();

  if (!allowedFormats.includes(fileExtension)) {
    return {
      valid: false,
      error: `Invalid file format. Only ${ALLOWED_FORMATS} are allowed. File: ${fileExtension}`
    };
  }

  return { valid: true, error: null };
};

/**
 * Get full image URL from relative path
 * @param {string} relativePath - Relative file path (e.g., 'profile/filename.jpg')
 * @returns {string} Full image URL
 */
export const getImageUrl = (relativePath) => {
  if (!relativePath) return null;

  // Remove leading slashes
  const cleanPath = relativePath.replace(/^\/+/, '');
  return `${IMAGE_BASE_URL}/uploads/${cleanPath}`;
};

/**
 * Upload single image file (e.g., profile photo)
 * @param {File} file - The file object from input
 * @param {string} category - Upload category ('profile', 'merchant', 'products')
 * @param {string} endpoint - API endpoint (e.g., '/api/auth/profile')
 * @param {object} additionalData - Additional form data to include
 * @param {function} onProgress - Progress callback function (optional)
 * @returns {Promise} Resolves to response data
 */
export const uploadSingleImage = async (
  file,
  category = 'profile',
  endpoint = '/api/auth/profile',
  additionalData = {},
  onProgress = null
) => {
  try {
    // Validate file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Create FormData
    const formData = new FormData();
    formData.append('photoUrl', file); // Field name should match backend

    // Add additional data if provided
    Object.keys(additionalData).forEach(key => {
      if (Array.isArray(additionalData[key])) {
        additionalData[key].forEach((item, index) => {
          formData.append(`${key}[${index}]`, item);
        });
      } else {
        formData.append(key, additionalData[key]);
      }
    });

    // Get auth token from localStorage
    const token = localStorage.getItem('accessToken');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // Upload with progress tracking
    const response = await axios.post(`${API_BASE_URL}${endpoint}`, formData, {
      headers: {
        ...headers,
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: onProgress ? (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      } : undefined
    });

    return response.data;
  } catch (error) {
    console.error('Image upload error:', error);
    throw error;
  }
};

/**
 * Upload multiple images (e.g., owner photo + business logo)
 * @param {object} files - Object with file fields { ownerPhoto: File, businessLogo: File }
 * @param {string} endpoint - API endpoint (e.g., '/api/merchant/create')
 * @param {object} additionalData - Additional form data to include
 * @param {function} onProgress - Progress callback function (optional)
 * @returns {Promise} Resolves to response data
 */
export const uploadMultipleImages = async (
  files = {},
  endpoint = '/api/merchant/create',
  additionalData = {},
  onProgress = null
) => {
  try {
    // Validate all files
    const fileEntries = Object.entries(files).filter(([_, file]) => file);
    for (const [fieldName, file] of fileEntries) {
      const validation = validateImageFile(file);
      if (!validation.valid) {
        throw new Error(`${fieldName}: ${validation.error}`);
      }
    }

    // Create FormData
    const formData = new FormData();

    // Add files
    fileEntries.forEach(([fieldName, file]) => {
      formData.append(fieldName, file);
    });

    // Add additional data
    Object.keys(additionalData).forEach(key => {
      if (Array.isArray(additionalData[key])) {
        additionalData[key].forEach((item, index) => {
          formData.append(`${key}`, item);
        });
      } else if (additionalData[key] !== null && additionalData[key] !== undefined) {
        formData.append(key, additionalData[key]);
      }
    });

    // Get auth token from localStorage
    const token = localStorage.getItem('accessToken');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // Upload with progress tracking
    const response = await axios.post(`${API_BASE_URL}${endpoint}`, formData, {
      headers: {
        ...headers,
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: onProgress ? (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      } : undefined
    });

    return response.data;
  } catch (error) {
    console.error('Multiple image upload error:', error);
    throw error;
  }
};

/**
 * Upload a single image file/blob to the generic /api/upload endpoint and
 * return its relative path (e.g. '/uploads/merchant/167...-photo.webp').
 * The server converts every image to WebP and resizes it per category
 * (see backend/src/utils/imageUpload.js).
 * @param {File|Blob} file - The file/blob to upload (e.g. output of an image cropper)
 * @param {string} category - Upload category ('profile', 'merchant', 'products', ...)
 * @param {string} [filename] - Filename to send (required for Blobs, which have no name)
 * @returns {Promise<string>} The uploaded image's relative path
 */
export const uploadImageToServer = async (file, category = 'general', filename) => {
  // Note: not using validateImageFile() here — cropped images arrive as nameless
  // Blobs, and its extension check assumes file.name exists. The server already
  // validates size/format (imageUpload.js fileFilter + MAX_FILE_SIZE).
  const name = filename || file.name || `upload.${file.type?.split('/')[1] || 'jpg'}`;
  const formData = new FormData();
  formData.append('image', file, name);

  const { data } = await api.post(`/api/upload/${category}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

  if (!data?.success || !data?.data?.path) {
    throw new Error(data?.message || 'Image upload failed');
  }

  return data.data.path;
};

/**
 * Read image file as data URL for preview
 * @param {File} file - The file object from input
 * @returns {Promise} Resolves to data URL string
 */
export const readImageAsDataUrl = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

/**
 * Get image preview URL (either data URL or server URL)
 * @param {File|string} source - Either a File object or relative image path
 * @returns {Promise} Resolves to image URL for preview
 */
export const getImagePreview = async (source) => {
  if (source instanceof File) {
    // If it's a file, read as data URL
    return readImageAsDataUrl(source);
  } else if (typeof source === 'string') {
    // If it's a string path, construct full URL
    return getImageUrl(source);
  }
  return null;
};

/**
 * Format file size in human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Create image input change handler
 * @param {function} onFileSelect - Callback when file is selected
 * @returns {function} Event handler for file input
 */
export const createImageInputHandler = (onFileSelect) => {
  return async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      onFileSelect({ file: null, error: validation.error, preview: null });
      return;
    }

    try {
      const preview = await getImagePreview(file);
      onFileSelect({ file, error: null, preview });
    } catch (error) {
      onFileSelect({ file: null, error: error.message, preview: null });
    }
  };
};

export default {
  validateImageFile,
  getImageUrl,
  uploadSingleImage,
  uploadMultipleImages,
  readImageAsDataUrl,
  getImagePreview,
  formatFileSize,
  createImageInputHandler,
  MAX_FILE_SIZE,
  ALLOWED_FORMATS
};
